import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Typography,
  Box,
  Divider,
  IconButton
} from '@mui/material';
import {
  PhotoCamera as CameraIcon,
  Event as EventIcon,
  Campaign as CampaignIcon,
  Chat as ChatIcon,
  Restaurant as RecipeIcon,
  Description as DocIcon,
  PhotoLibrary as PhotosIcon,
  People as PeopleIcon,
  AccountTree as TreeIcon,
  HealthAndSafety as HealthIcon,
  Recommend as RecommendIcon,
  Flag as GoalIcon,
  Celebration as FunIcon,
  Warning as EmergencyIcon,
  Settings as SettingsIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { getFirestore, doc, getDoc, updateDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import logger from '../../utils/logger';

// All available quick actions
export const ALL_QUICK_ACTIONS = [
  { id: 'add-photo', label: 'Add Photo', icon: CameraIcon, color: '#4caf50', path: '/photos?action=upload' },
  { id: 'create-event', label: 'Create Event', icon: EventIcon, color: '#9c27b0', path: '/events?action=create' },
  { id: 'announcement', label: 'Announcement', icon: CampaignIcon, color: '#2196f3', path: '/announcements?action=create' },
  { id: 'discussion', label: 'Discussion', icon: ChatIcon, color: '#ff9800', path: '/discussions?action=create' },
  { id: 'add-recipe', label: 'Add Recipe', icon: RecipeIcon, color: '#f44336', path: '/recipes?action=create' },
  { id: 'upload-doc', label: 'Upload Doc', icon: DocIcon, color: '#00bcd4', path: '/documents?action=upload' },
  { id: 'view-photos', label: 'View Photos', icon: PhotosIcon, color: '#7b1fa2', path: '/photos' },
  { id: 'view-contacts', label: 'View Contacts', icon: PeopleIcon, color: '#388e3c', path: '/contacts' },
  { id: 'family-tree', label: 'Family Tree', icon: TreeIcon, color: '#00796b', path: '/family-tree' },
  { id: 'health-update', label: 'Health Update', icon: HealthIcon, color: '#d32f2f', path: '/health-updates?action=create' },
  { id: 'recommendation', label: 'Add Recommendation', icon: RecommendIcon, color: '#0097a7', path: '/recommendations?action=create' },
  { id: 'family-goal', label: 'Family Goal', icon: GoalIcon, color: '#ffc107', path: '/goals?action=create' },
  { id: 'fun-social', label: 'Fun & Social', icon: FunIcon, color: '#e91e63', path: '/fun-social?action=create' },
  { id: 'emergency-info', label: 'Emergency Info', icon: EmergencyIcon, color: '#ff5722', path: '/emergency-info' }
];

// Default quick actions (shown by default)
export const DEFAULT_QUICK_ACTIONS = [
  'add-photo',
  'create-event',
  'announcement',
  'discussion',
  'add-recipe',
  'upload-doc'
];

// Storage key for localStorage
const STORAGE_KEY = 'familyforlife_quick_actions';

// Get saved quick actions from localStorage (fast local cache)
export const getSavedQuickActions = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Validate that saved IDs exist in ALL_QUICK_ACTIONS
      return parsed.filter(id => ALL_QUICK_ACTIONS.some(a => a.id === id));
    }
  } catch (e) {
    logger.error('Error loading quick actions preferences:', e);
  }
  return DEFAULT_QUICK_ACTIONS;
};

// Load quick actions from Firestore (syncs across devices)
export const loadQuickActionsFromFirestore = async () => {
  try {
    const auth = getAuth();
    const userId = auth.currentUser?.uid;
    if (!userId) return null;

    const db = getFirestore();
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      const saved = userDoc.data().preferences?.quickActions;
      if (Array.isArray(saved)) {
        const validated = saved.filter(id => ALL_QUICK_ACTIONS.some(a => a.id === id));
        // Update local cache
        localStorage.setItem(STORAGE_KEY, JSON.stringify(validated));
        return validated;
      }
    }
  } catch (e) {
    logger.error('Error loading quick actions from Firestore:', e);
  }
  return null;
};

// Save quick actions to localStorage AND Firestore
export const saveQuickActions = async (actionIds) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(actionIds));

    // Also save to Firestore for cross-device sync
    const auth = getAuth();
    const userId = auth.currentUser?.uid;
    if (userId) {
      const db = getFirestore();
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        'preferences.quickActions': actionIds
      });
    }
  } catch (e) {
    logger.error('Error saving quick actions preferences:', e);
  }
};

// Get quick action objects from IDs
export const getQuickActionsByIds = (ids) => {
  return ids
    .map(id => ALL_QUICK_ACTIONS.find(a => a.id === id))
    .filter(Boolean);
};

/**
 * QuickActionsConfig Dialog
 * Allows users to customize which quick actions appear on their dashboard
 */
const QuickActionsConfig = ({ open, onClose }) => {
  const [selectedActions, setSelectedActions] = useState([]);

  useEffect(() => {
    if (open) {
      // Load from local first (fast), then sync from Firestore
      setSelectedActions(getSavedQuickActions());
      loadQuickActionsFromFirestore().then(firestoreActions => {
        if (firestoreActions) {
          setSelectedActions(firestoreActions);
        }
      });
    }
  }, [open]);

  const handleToggle = (actionId) => {
    setSelectedActions(prev => {
      if (prev.includes(actionId)) {
        return prev.filter(id => id !== actionId);
      } else {
        return [...prev, actionId];
      }
    });
  };

  const handleSave = () => {
    saveQuickActions(selectedActions);
    onClose(true); // Pass true to indicate changes were saved
  };

  const handleReset = () => {
    setSelectedActions(DEFAULT_QUICK_ACTIONS);
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SettingsIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Customize Quick Actions</Typography>
          </Box>
          <IconButton onClick={() => onClose(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select which quick actions to show on your Home page. You can select up to 8 actions.
        </Typography>
        <List dense>
          {ALL_QUICK_ACTIONS.map((action) => {
            const IconComponent = action.icon;
            const isSelected = selectedActions.includes(action.id);
            const isDisabled = !isSelected && selectedActions.length >= 8;

            return (
              <ListItem
                key={action.id}
                button
                onClick={() => !isDisabled && handleToggle(action.id)}
                disabled={isDisabled}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  bgcolor: isSelected ? 'action.selected' : 'transparent',
                  '&:hover': {
                    bgcolor: isSelected ? 'action.selected' : 'action.hover'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Checkbox
                    edge="start"
                    checked={isSelected}
                    disabled={isDisabled}
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <IconComponent sx={{ color: action.color }} />
                </ListItemIcon>
                <ListItemText
                  primary={action.label}
                  primaryTypographyProps={{
                    fontWeight: isSelected ? 600 : 400
                  }}
                />
              </ListItem>
            );
          })}
        </List>
        <Typography variant="caption" color="text.secondary">
          {selectedActions.length} of 8 actions selected
        </Typography>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleReset} color="inherit">
          Reset to Default
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={() => onClose(false)}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={selectedActions.length === 0}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickActionsConfig;
